import {
  Button,
  CardMedia,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  LabIconsUploadIcon,
  RefreshIcon,
  XCloseIcon
} from '@customink/pigment-react';
import {Fade, Slide} from '@mui/material';
import Box from '@mui/material/Box';
import {useRollbar} from '@rollbar/react';
import React, {useState} from 'react';
import DropZone from '../../components/DropZone';
import useBreakpoint from '../../hooks/useBreakpoint';
import {uploadFile} from '../../src/apis/upload_service_client';
import {createCustomDesignPreviewUploadUrl} from '../../utils/images';

const slideTransition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});
const fadeTransition = React.forwardRef(function Transition(props, ref) {
  return <Fade ref={ref} {...props} />;
});

function UploadModal({
  displayModal,
  fireMetricsEvent,
  recentUploads,
  setCustomDesignPreview,
  setDisplayModal,
  setRecentUploads
}) {
  const dialogTitle = 'Preview a Design';
  const dropZoneFileDesc = 'JPG, PNG, EPS, AI and PDF (Max 5 MB)';
  const isMobile = useBreakpoint() === 'sm';
  const rollbar = useRollbar();
  const [isUploading, setIsUploading] = useState(false);

  const handleModalClose = () => {
    setDisplayModal(false);
    setIsUploading(false);
  };
  const handleImageUpload = (file) => {
    setIsUploading(true);
    uploadFile(file)
      .then((data) => {
        const designImageUrl = createCustomDesignPreviewUploadUrl(data);
        const designObject = {data, url: designImageUrl};
        setCustomDesignPreview(designObject);
        setRecentUploads((currentRecentUploads) => [
          designObject,
          ...currentRecentUploads
        ]);
        fireMetricsEvent('catalog/upload/success');
        handleModalClose();
      })
      .catch((error) => {
        fireMetricsEvent('catalog/upload/failure');
        rollbar.warning(
          'Error uploading preview upload to UploadService',
          error
        );
        handleModalClose();
      });
  };
  const handleDropZoneClick = (file) => {
    fireMetricsEvent('catalog/upload/browse/dropzone');
    handleImageUpload(file);
  };
  const handleButtonClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    fireMetricsEvent('catalog/upload/browse/button');
    const input = event.currentTarget.parentNode.querySelector(
      '#pc-styles-uploadbar-modal-input'
    );
    input.click();
  };
  const handleInputChange = (event) => {
    event.preventDefault();
    handleImageUpload(event.target.files[0]);
  };
  const selectRecentUpload = (index = 0) => {
    fireMetricsEvent('catalog/upload/add-recent');
    setCustomDesignPreview(recentUploads[index]);
    handleModalClose();
  };
  const removeRecentUpload = (index = 0) => {
    fireMetricsEvent('catalog/upload/remove-recent');
    setRecentUploads((currentRecentUploads) =>
      currentRecentUploads.filter((_, i) => i !== index)
    );
  };

  const dialogContent = (
    <DialogContent>
      <div className="pc-Modal pc-Styles-uploadBar-modal">
        <div className="pc-Modal pc-Styles-uploadBar-modal-uploadSection">
          <span>High resolution artwork will look best.</span>
          <input
            id="pc-styles-uploadbar-modal-input"
            className="pc-DropZone-input"
            type="file"
            accept="image/jpeg,image/png,.eps,.pdf,.ai"
            autoComplete="off"
            tabIndex="-1"
            onChange={handleInputChange}
          />
          <div className="pc-Styles-uploadBar-modal-uploadSection-dropzone">
            <DropZone
              className="pc-Styles-uploadBar-modal-uploadSection-dropzone"
              accept="image/jpeg,image/png,.eps,.pdf,.ai"
              supportedFilesDescription={dropZoneFileDesc}
              onUpload={handleDropZoneClick}
              isUploading={isUploading}
            />
          </div>
          <Button
            className="pc-Styles-uploadBar-modal-uploadSection-button"
            variant="primary"
            startIcon={
              isUploading ? (
                <RefreshIcon className="spin" />
              ) : (
                <LabIconsUploadIcon />
              )
            }
            size="large"
            onClick={handleButtonClick}
            disabled={isUploading}>
            Upload From Your Device
          </Button>
        </div>
        {recentUploads.length > 0 && (
          <div className="pc-Modal pc-Styles-uploadBar-modal-recentUploadsSection">
            <h2>Recent Uploads</h2>
            <Grid
              container
              spacing={1}
              columns={{xs: 4}}
              justifyContent="flex-start"
              alignItems="center"
              className="pc-Styles-uploadBar-modal-grid">
              {recentUploads.map((design, index) => {
                return (
                  <Grid
                    key={`recently-uploaded-image-${design?.data?.url}`}
                    item
                    xs={3}
                    className="pc-Styles-uploadBar-modal-grid-item">
                    <IconButton
                      fill
                      aria-label="anchorSmall"
                      className="pc-Styles-uploadBar-modal-grid-item-close"
                      onClick={() => removeRecentUpload(index)}>
                      <XCloseIcon fontSize="inherit" />
                    </IconButton>
                    <Box
                      className="pc-Styles-uploadBar-modal-grid-item-image"
                      onClick={(_event) => selectRecentUpload(index)}>
                      <CardMedia component="img" image={design.url} />
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </div>
        )}
      </div>
    </DialogContent>
  );

  return (
    <Dialog
      className={`pc-Styles-uploadBar-modal-dialog ${isMobile ? 'mobile' : ''}`}
      onClose={handleModalClose}
      title={dialogTitle}
      open={displayModal}
      TransitionComponent={isMobile ? slideTransition : fadeTransition}>
      {dialogContent}
    </Dialog>
  );
}

export default UploadModal;
